import React, { useState } from 'react';
import {
  useStripe,
  useElements,
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement
} from '@stripe/react-stripe-js';
import { LockClosedIcon } from '@heroicons/react/24/solid';

interface PaymentFormProps {
  onSuccess: (paymentMethodId: string) => void;
  onError: (error: string) => void;
  isSubmitting: boolean;
  setIsSubmitting: (submitting: boolean) => void;
  organizationName: string;
  customerEmail: string;
  submitLabel?: string;
  submittingLabel?: string;
  /** When provided, uses stripe.confirmCardSetup() which attaches the PM to the customer via Stripe directly. */
  clientSecret?: string;
}

const cardElementOptions = {
  style: {
    base: {
      fontSize: '16px',
      color: '#424770',
      '::placeholder': {
        color: '#aab7c4',
      },
      padding: '12px',
    },
    invalid: {
      color: '#9e2146',
    },
  },
};

const PaymentForm: React.FC<PaymentFormProps> = ({
  onSuccess,
  onError,
  isSubmitting,
  setIsSubmitting,
  organizationName,
  customerEmail,
  submitLabel = 'Save Payment Method & Complete Registration',
  submittingLabel = 'Securely Saving Payment Method...',
  clientSecret
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [cardErrors, setCardErrors] = useState<{ [key: string]: string }>({});
  const [postalCode, setPostalCode] = useState('');
  const [postalCodeTouched, setPostalCodeTouched] = useState(false);

  const isValidPostalCode = (code: string) => /^\d{5}(-\d{4})?$/.test(code);
  const postalCodeError = postalCodeTouched && postalCode.length > 0 && !isValidPostalCode(postalCode)
    ? 'Enter a valid 5-digit ZIP code'
    : postalCodeTouched && postalCode.length === 0
    ? 'ZIP code is required'
    : '';

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!stripe || !elements) {
      onError('Stripe has not loaded yet. Please try again.');
      return;
    }

    const cardNumberElement = elements.getElement(CardNumberElement);
    if (!cardNumberElement) {
      onError('Card information is not available. Please refresh and try again.');
      return;
    }

    setIsSubmitting(true);
    setCardErrors({});
    setPostalCodeTouched(true);

    if (!isValidPostalCode(postalCode)) {
      setIsSubmitting(false);
      return;
    }

    try {
      if (clientSecret) {
        // SetupIntent flow: confirms the card and attaches the PM to the
        // Stripe Customer automatically — no card data touches our backend.
        const { error, setupIntent } = await stripe.confirmCardSetup(clientSecret, {
          payment_method: {
            card: cardNumberElement,
            billing_details: {
              name: organizationName,
              email: customerEmail,
              address: {
                postal_code: postalCode,
              },
            },
          },
        });

        if (error) {
          onError(error.message || 'An error occurred while confirming your card.');
          return;
        }

        if (setupIntent?.payment_method) {
          const pmId = typeof setupIntent.payment_method === 'string'
            ? setupIntent.payment_method
            : setupIntent.payment_method.id;
          onSuccess(pmId);
        }
      } else {
        // Detached PaymentMethod flow (used during registration)
        const { error, paymentMethod } = await stripe.createPaymentMethod({
          type: 'card',
          card: cardNumberElement,
          billing_details: {
            name: organizationName,
            email: customerEmail,
            address: {
              postal_code: postalCode,
            },
          },
        });

        if (error) {
          onError(error.message || 'An error occurred while processing your payment information.');
          return;
        }

        if (paymentMethod) {
          onSuccess(paymentMethod.id);
        }
      }
    } catch (error) {
      onError('An unexpected error occurred. Please try again.');
      console.error('Payment method creation error:', error);
    }
  };

  const handleCardChange = (elementType: string) => (event: any) => {
    if (event.error) {
      setCardErrors(prev => ({
        ...prev,
        [elementType]: event.error.message
      }));
    } else {
      setCardErrors(prev => ({
        ...prev,
        [elementType]: ''
      }));
    }
  };

  return (
    <form onSubmit={handleSubmit} className="w-full">
      <div className="space-y-4">
        {/* Card Number */}
        <div>
          <label className="block text-sm font-medium mb-2" style={{ color: '#002457' }}>
            Card Number <span className="text-red-500">*</span>
          </label>
          <div className="border-2 border-gray-200 rounded-2xl p-3 focus-within:border-blue-500 transition-colors duration-200">
            <CardNumberElement
              options={cardElementOptions}
              onChange={handleCardChange('cardNumber')}
            />
          </div>
          {cardErrors.cardNumber && (
            <p className="text-red-500 text-xs mt-1">{cardErrors.cardNumber}</p>
          )}
        </div>

        {/* Expiry, CVC, and ZIP */}
        <div className="grid grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: '#002457' }}>
              Expiry Date <span className="text-red-500">*</span>
            </label>
            <div className="border-2 border-gray-200 rounded-2xl p-3 focus-within:border-blue-500 transition-colors duration-200">
              <CardExpiryElement
                options={cardElementOptions}
                onChange={handleCardChange('cardExpiry')}
              />
            </div>
            {cardErrors.cardExpiry && (
              <p className="text-red-500 text-xs mt-1">{cardErrors.cardExpiry}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: '#002457' }}>
              CVC <span className="text-red-500">*</span>
            </label>
            <div className="border-2 border-gray-200 rounded-2xl p-3 focus-within:border-blue-500 transition-colors duration-200">
              <CardCvcElement
                options={cardElementOptions}
                onChange={handleCardChange('cardCvc')}
              />
            </div>
            {cardErrors.cardCvc && (
              <p className="text-red-500 text-xs mt-1">{cardErrors.cardCvc}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: '#002457' }}>
              ZIP Code <span className="text-red-500">*</span>
            </label>
            <div className={`border-2 rounded-2xl p-3 transition-colors duration-200 ${
              postalCodeError ? 'border-red-300 focus-within:border-red-500' : 'border-gray-200 focus-within:border-blue-500'
            }`}>
              <input
                type="text"
                inputMode="numeric"
                maxLength={10}
                autoComplete="postal-code"
                placeholder="12345"
                value={postalCode}
                onChange={(e) => {
                  const val = e.target.value.replace(/[^\d-]/g, '');
                  setPostalCode(val);
                }}
                onBlur={() => setPostalCodeTouched(true)}
                className="w-full outline-none bg-transparent"
                style={{ color: postalCodeError ? '#9e2146' : '#424770', fontSize: '16px', lineHeight: '20.8px', padding: 0, margin: 0, border: 'none' }}
              />
            </div>
            {postalCodeError && (
              <p className="text-red-500 text-xs mt-1">{postalCodeError}</p>
            )}
          </div>
        </div>

        {/* Security Notice */}
        <div className="bg-green-50 border border-green-200 rounded-2xl p-4">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <LockClosedIcon className="h-5 w-5 text-green-400" />
            </div>
            <div className="ml-3">
              <p className="text-sm text-green-700">
                <strong>Secure & Encrypted:</strong> Your payment information is processed securely by Stripe and never stored on our servers.
              </p>
            </div>
          </div>
        </div>

        {/* Submit Button */}
        <button
          type="submit"
          disabled={!stripe || isSubmitting || !isValidPostalCode(postalCode)}
          className="px-8 py-4 rounded-full w-full font-medium text-base transition-all duration-200 ease-in-out disabled:opacity-50 disabled:cursor-not-allowed text-white shadow-lg hover:shadow-xl"
          style={{
            background: 'linear-gradient(135deg, #19B7DF 0%, #41EFAA 100%)',
            border: 'none'
          }}
        >
          {isSubmitting ? submittingLabel : submitLabel}
        </button>
      </div>
    </form>
  );
};

export default PaymentForm;
